<template>
  <div class="hw-split">
    <HrWorkforcePanel class="hw-bars-panel" title="Staff by branch" note="click a bar for details">
      <HrWorkforceLegend v-if="hasData(categoryLegend)" :items="categoryLegend" />
      <div ref="barsList" class="hw-bars" @scroll="fillBars">
        <HrWorkforceBarRow
          v-for="row in visibleBarRows"
          :key="row.key"
          :name="row.name"
          :title="barTitle(row)"
          :segments="toItems(row.summary.category, labels.categories)"
          :scale="maxBranchTotal"
          stacked
          :value="onlyCommaFormat(row.summary.total)"
          clickable
          :active="selectedBranch === row.key"
          @select="emit('select-branch', row.key)"
        />
      </div>
      <div v-if="hasMoreBars" class="hw-bars-foot hw-muted">Showing {{ visibleBarRows.length }} of {{ barRows.length }} · scroll for more</div>
    </HrWorkforcePanel>
    <HrWorkforcePanel>
      <div class="hw-selected">
        <div>
          <span class="hw-label">Selected</span>
          <div class="hw-selected-name">{{ selectedBranchName }}</div>
        </div>
        <div class="hw-selected-value">{{ onlyCommaFormat(current.total) }}<small>staff</small></div>
      </div>
      <div v-if="hasData(genderItems)" class="hw-label hw-section-title">Gender balance</div>
      <div v-if="hasData(genderItems)" class="hw-balance">
        <span
          v-for="item in genderItems"
          :key="item.key"
          class="hw-balance-part"
          :style="{ width: shareWidth(item.value, current.total), backgroundColor: item.color }"
          :title="item.label + ': ' + onlyCommaFormat(item.value) + ' (' + percentLabel(item.value, current.total) + ')'"
        ></span>
      </div>
      <div class="hw-group-grid">
        <div v-for="group in panelGroups" :key="group.title" class="hw-group">
          <div class="hw-label hw-section-title">{{ group.title }} · {{ group.items.length }}</div>
          <HrWorkforceLegend class="hw-group-list" :items="group.items" :total="current.total" />
        </div>
      </div>
    </HrWorkforcePanel>
  </div>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, toRef, watch } from 'vue'
import figureFormatter from '@/services/utils/figureFormatter'
import { hasData, notAssignedKey, percentLabel, shareWidth, toItems, useHrWorkforceItems } from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/hrWorkforce'
import HrWorkforcePanel from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/HrWorkforcePanel.vue'
import HrWorkforceLegend from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/HrWorkforceLegend.vue'
import HrWorkforceBarRow from '@/components/molecule/company/dashboard/dynamic-dashboard/hr-workforce/common/HrWorkforceBarRow.vue'

const props = defineProps({
  summary: { type: Object, required: true },
  current: { type: Object, required: true },
  labels: { type: Object, required: true },
  branchRows: { type: Array, required: true },
  selectedBranch: { type: String, required: true },
  selectedBranchName: { type: String, required: true }
})

const emit = defineEmits(['select-branch'])

const barPageSize = 15

const { onlyCommaFormat } = figureFormatter()
const { genderItems, categoryLegend, panelGroups } = useHrWorkforceItems(toRef(props, 'current'), toRef(props, 'labels'), toRef(props, 'summary'))

const barLimit = ref(barPageSize)
const barsList = ref(null)

const barRows = computed(() => [...props.branchRows].sort((a, b) => (a.key === notAssignedKey) - (b.key === notAssignedKey) || b.summary.total - a.summary.total))
const visibleBarRows = computed(() => barRows.value.slice(0, barLimit.value))
const hasMoreBars = computed(() => visibleBarRows.value.length < barRows.value.length)
const maxBranchTotal = computed(() => Math.max(1, ...props.branchRows.filter(row => row.key !== notAssignedKey).map(row => row.summary.total)))

const barTitle = (row) => toItems(row.summary.category, props.labels.categories).map(item => item.label + ' ' + item.value).join(' · ')

const fillBars = () => {
  const list = barsList.value
  if (!list || !hasMoreBars.value) return
  if (list.scrollTop + list.clientHeight < list.scrollHeight - 40) return
  barLimit.value += barPageSize
  nextTick(fillBars)
}

const barsObserver = new ResizeObserver(() => fillBars())

watch(barsList, (list) => {
  barsObserver.disconnect()
  if (!list) return
  barsObserver.observe(list)
})

onBeforeUnmount(() => barsObserver.disconnect())
</script>

<style scoped src="./common/hrWorkforce.css"></style>

<style scoped>
.hw-bars-panel {
  display: flex;
  flex-direction: column;
}
.hw-bars {
  flex: 1 1 auto;
  height: 0;
  min-height: 360px;
  overflow-y: auto;
  scrollbar-width: thin;
}
.hw-bars-foot {
  text-align: center;
  padding-top: 8px;
  border-top: 1px solid #f1f5f9;
}
.hw-selected {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  margin-bottom: 14px;
  border-radius: 10px;
  background: #f0fdfa;
  border: 1px solid #ccfbf1;
}
.hw-selected-name {
  font-weight: 700;
  color: #134e4a;
}
.hw-selected-value {
  font-size: 1.6rem;
  font-weight: 700;
  color: #0f766e;
  font-variant-numeric: tabular-nums;
}
.hw-selected-value small {
  font-size: 0.75rem;
  font-weight: 500;
  color: #64748b;
  margin-left: 4px;
}
.hw-section-title {
  font-weight: 700;
  margin-bottom: 6px;
}
.hw-balance {
  display: flex;
  gap: 2px;
  height: 14px;
  border-radius: 8px;
  overflow: hidden;
  background: #f1f5f9;
  margin-bottom: 16px;
}
.hw-balance-part {
  display: block;
  height: 100%;
}
.hw-group-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px 18px;
}
.hw-group-list {
  max-height: 220px;
  overflow-y: auto;
  scrollbar-width: thin;
}
@media (max-width: 991px) {
  .hw-bars {
    flex: none;
    height: auto;
    min-height: 0;
    max-height: 320px;
  }
}
@media (max-width: 575px) {
  .hw-group-grid {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
